import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AddDeliveryDialog from "./Dialogs/AddDeliveryDialog";
import EditDeliveryDialog from "./Dialogs/EditDeliveryDialog";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Swal from "sweetalert2";

const MenuDelivery = ({ token, fetchMenuData }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [newName, setNewName] = useState("");
  const [toggling, setToggling] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTarget, setMenuTarget] = useState(null);

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-deliveries`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDeliveries(data.deliveries || []);
    } catch (err) {
      console.error("Error fetching deliveries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, []);

  const handleAddSuccess = () => {
    fetchDeliveries();
    fetchMenuData();
  };

  const toggleDelivery = async (deliveryName, currentToggle) => {
    const result = await Swal.fire({
      title: `${currentToggle ? "Disable" : "Enable"} ${deliveryName}?`,
      text: `Are you sure you want to ${
        currentToggle ? "disable" : "enable"
      } this delivery method?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed",
    });

    if (!result.isConfirmed) return;

    try {
      setToggling(deliveryName);
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/toggle-delivery`,
        { deliveryName, toggle: !currentToggle },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchDeliveries();
      fetchMenuData();
    } catch (err) {
      console.error("Error toggling delivery:", err);
      Swal.fire("Error", "Failed to toggle delivery status.", "error");
    } finally {
      setToggling(null);
    }
  };

  const handleNameEdit = async (oldName, newName) => {
    if (!newName.trim() || newName === oldName) {
      setEditingName(null);
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/edit-delivery`,
        { oldName, newName },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEditingName(null);
      fetchDeliveries();
      fetchMenuData();
    } catch (err) {
      const message =
        err?.response?.data?.message || "Failed to update delivery name.";
      Swal.fire("Error", message, "error");
      console.error("Error editing delivery name:", err);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Delivery Methods</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddDialog(true)}
        >
          Add Delivery
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <CircularProgress />
          <Typography mt={2}>Fetching Deliveries...</Typography>
        </Box>
      ) : (
        <Box>
          {deliveries.map((delivery, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
                borderBottom: "1px solid #ccc",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {editingName === delivery.deliveryName ? (
                  <>
                    <TextField
                      size="small"
                      value={newName}
                      autoFocus
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleNameEdit(delivery.deliveryName, newName);
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleNameEdit(delivery.deliveryName, newName)
                      }
                      sx={{ color: "#2e7d32" }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setEditingName(null)}
                      sx={{ color: "#d32f2f" }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 500 }}>
                      {delivery.deliveryName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingName(delivery.deliveryName);
                        setNewName(delivery.deliveryName);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {toggling === delivery.deliveryName ? (
                  <CircularProgress size={20} />
                ) : (
                  <Switch
                    checked={delivery.toggle}
                    onChange={() =>
                      toggleDelivery(delivery.deliveryName, delivery.toggle)
                    }
                  />
                )}

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSelectedDelivery(delivery.deliveryName);
                    setOpenEditDialog(true);
                  }}
                >
                  Edit Menu Prices
                </Button>

                {/* 3-dot menu for delete */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setMenuTarget(delivery.deliveryName);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={
                    Boolean(anchorEl) && menuTarget === delivery.deliveryName
                  }
                  onClose={() => {
                    setAnchorEl(null);
                    setMenuTarget(null);
                  }}
                >
                  <MenuItem
                    onClick={async () => {
                      setAnchorEl(null);
                      setMenuTarget(null);
                      const confirm = await Swal.fire({
                        title: `Delete ${delivery.deliveryName}?`,
                        text: "This will only remove it from the menu schema.",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonText: "Yes, delete it!",
                      });

                      if (confirm.isConfirmed) {
                        try {
                          await axios.delete(
                            `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/delete-delivery`,
                            {
                              headers: { Authorization: `Bearer ${token}` },
                              data: { deliveryName: delivery.deliveryName },
                            }
                          );
                          fetchDeliveries();
                          fetchMenuData();
                        } catch (err) {
                          console.error("Error deleting delivery:", err);
                        }
                      }
                    }}
                    sx={{ color: "error.main" }}
                  >
                    Delete
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {openAddDialog && (
        <AddDeliveryDialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          onSuccess={handleAddSuccess}
          token={token}
        />
      )}

      {openEditDialog && (
        <EditDeliveryDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          deliveryName={selectedDelivery}
          token={token}
          onSuccess={() => {
            fetchDeliveries();
            fetchMenuData();
          }}
        />
      )}
    </Box>
  );
};

export default MenuDelivery;
